import React from "react";

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconPending() {
  return (
    <span className="w-5 h-5 rounded-full border-2 border-gray-700 flex-shrink-0 block" />
  );
}

function IconRunning() {
  return (
    <span className="w-5 h-5 rounded-full border-2 border-lime-500 border-t-transparent flex-shrink-0 block animate-spin" />
  );
}

function IconDone() {
  return (
    <span className="w-5 h-5 rounded-full bg-green-500/15 border border-green-600 flex items-center justify-center flex-shrink-0">
      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2 6l3 3 5-5" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function IconError() {
  return (
    <span className="w-5 h-5 rounded-full bg-red-500/15 border border-red-600 flex items-center justify-center flex-shrink-0">
      <svg width="8" height="8" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M3 3l6 6M9 3l-6 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function IconSkipped() {
  return (
    <span className="w-5 h-5 rounded-full border border-gray-800 flex items-center justify-center flex-shrink-0">
      <span className="text-gray-700 text-xs leading-none select-none">—</span>
    </span>
  );
}

const STAGE_ICON = {
  pending: <IconPending />,
  running: <IconRunning />,
  done:    <IconDone />,
  error:   <IconError />,
  skipped: <IconSkipped />,
};

const LABEL_COLOR = {
  pending: "text-silver-dark",
  running: "text-lime-400",
  done:    "text-silver-medium",
  error:   "text-red-400",
  skipped: "text-gray-700",
};

// Connector line between two adjacent stages — color reflects the upper stage status.
const CONNECTOR_COLOR = {
  done:    "bg-green-600/30",
  running: "bg-lime-500/20",
  error:   "bg-red-500/20",
  pending: "bg-gray-800",
  skipped: "bg-gray-800",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return null;
  const ms = new Date(finishedAt) - new Date(startedAt);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function runningDuration(startedAt) {
  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt);
  if (ms < 1000) return null;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * DeployStageIndicator
 *
 * Renders the ordered stages array from a DeployJob as a vertical step-by-step
 * pipeline view. Handles pending / running / done / error / skipped states with
 * icons, connector lines, and per-stage timing.
 *
 * Props:
 *   stages  — job.stages[] from the API response
 *   compact — boolean; when true, hides connector lines and trims bottom padding
 *             (useful for embedding in tight spaces)
 */
export default function DeployStageIndicator({ stages, compact = false }) {
  if (!Array.isArray(stages) || stages.length === 0) return null;

  const sorted = [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className={compact ? "" : "mb-5"}>
      <p className="text-[10px] text-silver-dark uppercase tracking-widest mb-3 font-semibold">
        Pipeline
      </p>

      <div className="flex flex-col">
        {sorted.map((stage, i) => {
          const isLast   = i === sorted.length - 1;
          const icon     = STAGE_ICON[stage.status] || STAGE_ICON.pending;
          const label    = LABEL_COLOR[stage.status] || LABEL_COLOR.pending;
          const line     = CONNECTOR_COLOR[stage.status] || CONNECTOR_COLOR.pending;
          const dur      = stage.status === "done"
            ? formatDuration(stage.startedAt, stage.finishedAt)
            : stage.status === "running"
              ? runningDuration(stage.startedAt)
              : null;

          return (
            <div key={stage.id} className="flex gap-3">
              {/* Left column: icon + vertical connector */}
              <div className="flex flex-col items-center" style={{ width: 20 }}>
                {icon}
                {!isLast && (
                  <div
                    className={`w-px flex-1 my-0.5 min-h-[14px] ${line}`}
                  />
                )}
              </div>

              {/* Right column: label + timing + error message */}
              <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-3"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-xs font-medium leading-5 ${label}`}>
                    {stage.label}
                  </span>

                  {stage.status === "running" && (
                    <span className="text-[10px] text-lime-600 font-mono">
                      {dur ? `${dur}…` : "running…"}
                    </span>
                  )}

                  {dur && stage.status !== "running" && (
                    <span className="text-[10px] text-silver-dark font-mono ml-auto">
                      {dur}
                    </span>
                  )}

                  {stage.status === "skipped" && (
                    <span className="text-[10px] text-gray-700 font-mono">skipped</span>
                  )}
                </div>

                {stage.message && stage.status === "error" && (
                  <p className="text-[10px] text-red-400 mt-0.5 leading-tight truncate">
                    {stage.message}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
